// Global RO / PIU filter plumbing.
//
// The NHAI views can be filtered by RO (Regional Office) and PIU (Project
// Implementation Unit). Rather than thread these params through every API call
// site, we register one axios request interceptor that appends `ro` / `piu`
// (read from localStorage) to every /webApi/ request. The backend treats both
// as optional and ignores them when a road has no RO/PIU mapping, so appending
// globally is safe even for endpoints that don't filter on them.
//
// The selection is persisted in localStorage so it survives the full-page
// reload the filter bar triggers on change (mirroring the org switcher).
import axios from "axios";

export const RO_KEY = "ro_filter";
export const PIU_KEY = "piu_filter";
export const WEEK_KEY = "week_filter";
// Sentinel stored when the user explicitly picks "All weeks": it counts as a
// chosen value (so the bar won't re-default to the latest week) but sends no
// period_label, i.e. no week restriction.
export const ALL_WEEKS = "__all__";

export const getRoFilter = () => {
  try { return window.localStorage.getItem(RO_KEY) || ""; } catch { return ""; }
};
export const getPiuFilter = () => {
  try { return window.localStorage.getItem(PIU_KEY) || ""; } catch { return ""; }
};
export const getWeekFilter = () => {
  try { return window.localStorage.getItem(WEEK_KEY) || ""; } catch { return ""; }
};

// Register exactly once, even under React StrictMode double-imports / HMR.
if (!window.__roPiuInterceptorRegistered) {
  window.__roPiuInterceptorRegistered = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || "";
      if (url.includes("/webApi/")) {
        const ro = getRoFilter();
        const piu = getPiuFilter();
        const week = getWeekFilter();
        if (ro || piu || (week && week !== ALL_WEEKS)) {
          config.params = { ...(config.params || {}) };
          if (ro && config.params.ro === undefined) config.params.ro = ro;
          if (piu && config.params.piu === undefined) config.params.piu = piu;
          if (week && week !== ALL_WEEKS && config.params.period_label === undefined) {
            config.params.period_label = week;
          }
        }
      }
    } catch {
      // never block a request because of filter plumbing
    }
    return config;
  });
}

// road_id -> {ro, piu} map, fetched once and cached for the session so any page
// can render an "RO" column from a row's road_id without a per-row request.
let _roadMapPromise = null;
export const getRoadMap = () => {
  if (!_roadMapPromise) {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    _roadMapPromise = axios
      .get(`${base}/project/ro_piu_filters`)
      .then((res) => res.data?.result?.road_map || {})
      .catch(() => ({}));
  }
  return _roadMapPromise;
};

// RO string for a road_id given a road_map (from getRoadMap), or "".
export const roOf = (roadMap, roadId) => (roadMap?.[roadId]?.ro) || "";

// Apply a new RO/PIU selection: persist it, drop the per-view caches the org
// switcher also clears (so stale data can't survive), and reload so every page
// refetches with the new filter. Passing "" clears that dimension.
// Shared teardown for any filter change: drop the per-view caches the org
// switcher also clears (so stale data can't survive) and reload so every page
// refetches with the new filter applied by the interceptor above.
const resetAndReload = () => {
  try {
    const ls = window.localStorage;
    for (let i = ls.length - 1; i >= 0; i--) {
      const k = ls.key(i);
      if (k && /^(gis_|road_|defect_)/.test(k)) ls.removeItem(k);
    }
    const ss = window.sessionStorage;
    for (let i = ss.length - 1; i >= 0; i--) {
      const k = ss.key(i);
      if (k && /^gis_/.test(k)) ss.removeItem(k);
    }
    window.dispatchEvent(new CustomEvent("roadvision:logout"));
  } catch {}
  window.location.reload();
};

export const applyRoPiuFilter = (ro, piu) => {
  try {
    if (ro) window.localStorage.setItem(RO_KEY, ro);
    else window.localStorage.removeItem(RO_KEY);
    if (piu) window.localStorage.setItem(PIU_KEY, piu);
    else window.localStorage.removeItem(PIU_KEY);
  } catch {}
  resetAndReload();
};

// Apply a new weekly-survey selection (a period_label, or ALL_WEEKS). Passing ""
// clears the stored week so the bar re-defaults to the latest week next load.
export const applyWeekFilter = (week) => {
  try {
    if (week) window.localStorage.setItem(WEEK_KEY, week);
    else window.localStorage.removeItem(WEEK_KEY);
  } catch {}
  resetAndReload();
};
